import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Controller, useForm } from 'react-hook-form';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import SelectDropdown from 'common/components/Select/SelectDropdown';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setRegInvententoryCreateDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setSelectedGroupData } from 'common/store/reducer/regulationInventoryReducer';

import { risklevel } from 'features/RiskProfile/Taxonomy/RelationMapping/mappingUtils';

import { createGroupDataApi, updateGroupDataApi } from './api';
import { IGroupsTableList, ICitationGroup } from './api/type-def';

import styles from './RegulatoryInventoryAddGroup.module.scss';

interface IRegulatoryInventoryAddGroup {
	onAddHandler?: (groupIds: ICitationGroup[]) => void;
}

const { OK, BAD_REQUEST, CREATED } = HTTP_STATUS_CODES;

const RegulatoryInventoryAddGroup = (props: IRegulatoryInventoryAddGroup) => {
	const { onAddHandler } = props;
	const selectedGroup = useSelector(
		(state: RootState) => state.regulationInventory.selectedGroupData,
	);

	const emptyGroup = {
		groupId: '',
		groupName: '',
		description: '',
		createdBy: '',
		priority: '',
		taskStatus: '',
		reviewByDate: '',
		taskId: null,
		assignedUserId: '',
		assignedUserName: '',
		createdByName: '',
		businessUnitDetailDTOS: [
			{
				businessUnitId: '',
				businessUnitName: '',
				assignedUserId: '',
				assignedUserName: '',
			},
		],
	};

	const [mode, setMode] = useState('Add');

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const { drawerCloseIconColor } = styles;

	const {
		control,
		getValues,
		reset,
		formState: { isDirty, isValid },
	} = useForm<IGroupsTableList>({
		mode: 'onChange',
		defaultValues: selectedGroup || emptyGroup,
	});

	const onCancelHandler = () => {
		reset({ ...emptyGroup });
		dispatch(setSelectedGroupData({ ...emptyGroup }));
		drawerCloseHandler();
	};

	const dispatch = useDispatch();

	const addGroupDrawerState: any = useSelector(
		(state: RootState) => state.common.regInvententoryCreateDrawer,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const drawerCloseHandler = useCallback(() => {
		dispatch(setRegInvententoryCreateDrawer(!addGroupDrawerState));

		reset({ ...emptyGroup });
		dispatch(setSelectedGroupData({ ...emptyGroup }));
	}, [addGroupDrawerState, reset]);

	useEffect(() => {
		if (selectedGroup?.groupId) {
			setMode('Edit');
			const group = {
				groupName: selectedGroup.groupName,
				description: selectedGroup.description,
				priority: selectedGroup.priority,
			};
			reset(group);
		}
		if (mode !== 'Add') {
			setMode('Add');
			reset(emptyGroup);
		}
	}, [selectedGroup]);

	const onSaveHandler = useCallback(async () => {
		const headers = {
			...getValues(),
			loggedInUser: loggedInUser?.userId || '',
			metadataTag: 'Tag',
		};

		try {
			let response;
			if (selectedGroup?.groupId) {
				response = await updateGroupDataApi(headers, selectedGroup.groupId);
			} else {
				response = await createGroupDataApi(headers);
			}

			if (
				response &&
				(response.statusCode === CREATED || response.statusCode === OK)
			) {
				dispatch(
					setToastMessage({
						message: 'The Group has been successfully created/updated',
						severity: 'success',
						isToastOpen: true,
					}),
				);
				const groups: ICitationGroup[] = [];
				const apiResponse: any = response?.responseBody;
				groups.push({
					groupId: apiResponse.id,
					groupName: apiResponse.groupName,
				});
				if (onAddHandler) {
					onAddHandler(groups);
				}
				drawerCloseHandler();
			} else if (response?.statusCode === BAD_REQUEST) {
				const { errorBody }: any = response;
				const errorMsg = errorBody?.errorMessage as string;
				dispatch(
					setToastMessage({
						message: errorMsg,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else {
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} catch (error) {
			const { errorBody }: any = error;
			const errorMsg = errorBody?.errorMessage as string;
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	}, []);

	const getHeaderText = () => {
		if (mode === 'Add') {
			return 'Create Group';
		}
		const groupName = selectedGroup?.groupName || '';
		const truncatedGroupName = groupName.slice(0, 30);
		const ellipsis = groupName.length > 30 ? '...' : '';

		return `Edit Group - ${truncatedGroupName}${ellipsis}`;
	};

	return (
		<Box
			className={styles['detailed-container']}
			sx={{ width: '100vh' }}
			role="presentation"
			data-testid="groups-container"
		>
			<div className={styles['groups-top-div']}>
				<div
					className={styles['groups-text-background']}
					data-testid="groups-top-div"
				>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						data-testid="close-button-stack"
					>
						<IconButton
							size="large"
							onClick={drawerCloseHandler}
							data-testid="close-groups-btn"
						>
							<DrawerClose fill={drawerCloseIconColor} />
						</IconButton>
					</Stack>

					<Box className={styles['groups-text-box']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography
								className={styles['groups-text-name']}
								data-testid="riskGpt-text"
							>
								{getHeaderText()}
							</Typography>
						</Stack>
					</Box>
				</div>
			</div>

			<Grid
				item
				className={styles['create-form-layout']}
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
			>
				<form>
					<Grid container>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="groupName"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Please enter Group name',
									},
									validate: (value) => !!value.trim(),
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<TextFieldWithLabel
										label="Group Name"
										name={name}
										id="task-name"
										placeholder="Enter the Group name"
										value={value}
										inputProps={{
											'data-testid': 'group-name',
										}}
										onChange={onChange}
										error={!!error}
										required
										helperText={error ? error.message : null}
										className={styles['groups-field-width']}
										wordLimit={75}
										positionClass={styles['word-limit-position']}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="description"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Please enter Group description',
									},
									validate: (value) => !!value.trim(),
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<TextFieldWithLabel
										label="Group Description"
										name={name}
										id="group-description"
										inputProps={{
											'data-testid': 'group-description',
										}}
										placeholder="Enter the Group description"
										value={value}
										onChange={onChange}
										multiLine
										required
										error={!!error}
										helperText={error ? error.message : null}
										className={styles['groups-field-width']}
										wordLimit={1000}
										positionClass={styles['word-limit-position-desc']}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="priority"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Please select priority',
									},
									validate: (value) => !!value.trim(),
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<SelectDropdown
										label="Priority"
										name={name}
										id="task-status"
										options={risklevel}
										inputProps={{
											'data-testid': 'group-priority',
										}}
										error={!!error}
										required
										helperText={error ? error.message : null}
										value={value}
										onChange={onChange}
										className={styles['groups-field-dropdown-priority']}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</form>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={styles['btn-position']}
			>
				<Stack
					direction="row"
					className={styles['btn-groups-button']}
					spacing={2}
				>
					<ButtonComp
						btnType="secondary"
						data-testid="cancel-btn"
						clickHandler={onCancelHandler}
						compClass={styles['btn-cancel']}
					>
						Cancel
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						disabled={!isDirty || !isValid}
						clickHandler={onSaveHandler}
						data-testid="save-btn"
					>
						Save
					</ButtonComp>
				</Stack>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};

export default RegulatoryInventoryAddGroup;
